import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowDown,
  BookOpen,
  Building2,
  Calculator,
  CheckCircle2,
  CheckSquare,
  Circle,
  Database,
  Eye,
  FileOutput,
  FileText,
  Info,
  Play,
  Shield,
  TrendingUp,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useSessionSwitcher } from "@/contexts/SessionSwitcherContext";
import {
  useNotificationGuideProgress,
  type NotificationStep,
} from "@/hooks/api/query/useNotificationGuideProgress";
import type { GuideStepStatus } from "@/types/guide-step";
import type { EntityData } from "@/types/entity";
import { useIsMobile } from "../hooks/use-mobile";
import { useOnboardingModal } from "@/contexts/OnboardingModalContext";
import { getNotificationPdfStatus } from "@/utils/guideProgressCalculator";

type StepConfig = {
  id: number;
  title: string;
  descriere: string;
  icon: React.ElementType;
  details: string[];
};

const stepsConfig: StepConfig[] = [
  {
    id: 1,
    title: "Preluarea Automată (Motorul CUI)",
    descriere:
      "Introduci CUI-ul firmei. NIS2PASS interoghează automat baza de date ONRC pentru CAEN-uri și verifică prezența în listele ANCOM/ANRE/MS.",
    icon: Database,
    details: [
      "Interogare API ONRC",
      "Verificare liste ANCOM",
      "Verificare liste ANRE",
      "Verificare liste MS",
    ],
  },
  {
    id: 2,
    title: "Calculul Dimensiunii",
    descriere:
      "Introduci numărul de salariați și cifra de afaceri. Algoritmul NIS2PASS determină dacă ești Micro, Mică, Mijlocie sau Mare conform Legii 346/2004.",
    icon: Calculator,
    details: [
      "Nr. salariați",
      "Cifra de afaceri",
      "Total active",
      "Clasificare automată",
    ],
  },
  {
    id: 3,
    title: "Rating-ul de Certitudine",
    descriere:
      "Aplicația îți va afișa serviciile găsite cu un scor de certitudine (ex: 95% pentru CAEN principal). Tu doar confirmi sau adaugi servicii lipsă.",
    icon: TrendingUp,
    details: [
      "Scor certitudine CAEN principal",
      "Matching servicii NIS2",
      "Sugestii servicii adiționale",
    ],
  },
  {
    id: 4,
    title: "Clasificarea NIS2",
    descriere:
      "Pe baza dimensiunii și serviciilor selectate, aplicația determină automat clasificarea entității: Esențială, Importantă sau în afara scopului NIS2.",
    icon: Shield,
    details: ["Entitate Esențială", "Entitate Importantă", "În afara scopului"],
  },
  {
    id: 5,
    title: "Generarea PDF-ului",
    descriere:
      "Aplicația transformă datele în formatul oficial DNSC. Descarci, semnezi electronic și trimiți pe e-mailul evidenta@dnsc.ro.",
    icon: FileOutput,
    details: [
      "Format oficial DNSC",
      "Semnătură electronică",
      "Transmitere email",
    ],
  },
];

function getStatusStyles(status: GuideStepStatus) {
  switch (status) {
    case "completed":
      return {
        iconBg: "bg-blue-600",
        cardBorder: "border-blue-200",
        showCompletedBadge: true,
        showInProgressBadge: false,
      };
    case "in_progress":
      return {
        iconBg: "bg-blue-500",
        cardBorder: "border-blue-400 ring-2 ring-blue-400 ring-offset-2",
        showCompletedBadge: false,
        showInProgressBadge: true,
      };
    case "not_started":
    default:
      return {
        iconBg: "bg-slate-400",
        cardBorder: "border-slate-200",
        showCompletedBadge: false,
        showInProgressBadge: false,
      };
  }
}

function WorkflowStepIcon({ status }: { status: GuideStepStatus }) {
  if (status === "completed") {
    return <CheckCircle2 size={16} className="text-emerald-400" />;
  }
  if (status === "in_progress") {
    return <Circle size={16} className="text-blue-400 animate-pulse" />;
  }
  return <Circle size={16} className="text-slate-500" />;
}

function WorkflowPanel({
  steps,
  onNavigate,
  entityData,
}: {
  steps: {
    step1: NotificationStep;
    step2: NotificationStep;
    step3: NotificationStep;
    step4: NotificationStep;
    step5: NotificationStep;
  };
  onNavigate: (path: string) => void;
  entityData: EntityData | null | undefined;
}) {
  const allCompleted =
    steps.step1.status === "completed" &&
    steps.step2.status === "completed" &&
    steps.step3.status === "completed" &&
    steps.step4.status === "completed" &&
    steps.step5.status === "completed";

  const anyInProgress =
    steps.step1.status === "in_progress" ||
    steps.step2.status === "in_progress" ||
    steps.step3.status === "in_progress" ||
    steps.step4.status === "in_progress" ||
    steps.step5.status === "in_progress";

  const getButtonConfig = () => {
    if (allCompleted) {
      return {
        text: "Revizuiește Notificarea",
        icon: Eye,
        className: "bg-emerald-600 hover:bg-emerald-700",
      };
    }
    if (anyInProgress) {
      return {
        text: "Continuă Notificarea",
        icon: Play,
        className: "bg-blue-600 hover:bg-blue-700",
      };
    }
    return {
      text: "Start Modul Notificare",
      icon: Play,
      className: "bg-blue-600 hover:bg-blue-700",
    };
  };

  const buttonConfig = getButtonConfig();
  const ButtonIcon = buttonConfig.icon;

  return (
    <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 text-white">
      <h2 className="font-heading font-semibold md:text-lg text-base md:mb-6 mb-4">
        Workflow Smart Search
      </h2>

      <div className="md:space-y-4 space-y-2">
        <div
          className={`rounded-xl p-4 ${steps.step1.status === "completed" ? "bg-emerald-500/20 border border-emerald-400/30" : "bg-white/10"}`}
        >
          <p className="text-xs text-slate-400 mb-2">INPUT</p>
          <div className="flex items-center gap-3">
            <Building2 size={24} />
            <div className="flex-1">
              <p className="font-mono md:text-lg text-sm">
                CUI: {entityData?.cui || "—"}
              </p>
              <p className="md:text-sm text-xs text-slate-400">
                Cod Unic de Identificare
              </p>
            </div>
            <WorkflowStepIcon status={steps.step1.status} />
          </div>
        </div>

        <div className="flex justify-center">
          <ArrowDown size={24} className="text-slate-500" />
        </div>

        <div className="bg-blue-500/20 rounded-xl p-4 border border-blue-400/30">
          <p className="text-xs text-blue-300 mb-2">PROCESARE</p>
          <div className="space-y-2">
            <div className="flex items-center gap-2 md:text-sm text-xs">
              <WorkflowStepIcon status={steps.step2.status} />
              <span>Dimensiune → Calcul automat</span>
            </div>
            <div className="flex items-center gap-2 md:text-sm text-xs">
              <WorkflowStepIcon status={steps.step3.status} />
              <span>CAEN Matching → Servicii NIS2</span>
            </div>
            <div className="flex items-center gap-2 md:text-sm text-xs">
              <WorkflowStepIcon status={steps.step4.status} />
              <span>Clasificare → Esențială/Importantă</span>
            </div>
          </div>
        </div>

        <div className="flex justify-center">
          <ArrowDown size={24} className="text-slate-500" />
        </div>

        <div
          className={`rounded-xl p-4 border ${steps.step5.status === "completed" ? "bg-emerald-500/20 border-emerald-400/30" : "bg-slate-500/20 border-slate-400/30"}`}
        >
          <p
            className={`text-xs mb-2 ${steps.step5.status === "completed" ? "text-emerald-300" : "text-slate-400"}`}
          >
            OUTPUT
          </p>
          <div className="flex items-center gap-3">
            <FileText
              size={24}
              className={
                steps.step5.status === "completed"
                  ? "text-emerald-400"
                  : "text-slate-400"
              }
            />
            <div className="flex-1 md:text-sm text-xs">
              <p className="font-medium md:text-sm text-xs">
                Formular Notificare.pdf
              </p>
              <p className="md:text-sm text-xs text-slate-400">
                Format oficial DNSC
              </p>
            </div>
            <WorkflowStepIcon status={steps.step5.status} />
          </div>
        </div>
      </div>

      <button
        onClick={() => onNavigate("/identificare-raportare")}
        className={`w-full mt-6 py-3 md:py-4 ${buttonConfig.className} text-sm md:text-base rounded-xl font-medium flex items-center justify-center gap-2 transition-colors`}
      >
        <ButtonIcon size={20} />
        {buttonConfig.text}
      </button>
    </div>
  );
}

function NotificationStepCard({
  config,
  stepData,
}: {
  config: StepConfig;
  stepData: NotificationStep;
}) {
  const isMobile = useIsMobile();
  const StepIcon = config.icon;
  const styles = getStatusStyles(stepData.status);

  return (
    <div
      className={`bg-white rounded-xl border ${styles.cardBorder} p-5 hover:shadow-md transition-shadow`}
    >
      <div className="flex items-start gap-4">
        <div className="relative">
          <div
            className={`md:w-12 w-10 md:h-12 h-10 ${styles.iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}
          >
            <StepIcon size={isMobile ? 16 : 24} className="text-white" />
          </div>
          {styles.showCompletedBadge && (
            <div className="absolute -top-1 -right-1 md:w-5 w-4 md:h-5 h-4 bg-blue-700 rounded-full flex items-center justify-center border-2 border-white">
              <CheckCircle2 size={isMobile ? 10 : 12} className="text-white" />
            </div>
          )}
          {styles.showInProgressBadge && (
            <div className="absolute -top-1 -right-1 md:w-5 w-4 md:h-5 h-4 bg-blue-600 rounded-full flex items-center justify-center border-2 border-white animate-pulse">
              <Circle
                size={isMobile ? 6 : 8}
                className="text-white fill-white"
              />
            </div>
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded">
              PASUL {config.id}
            </span>
            {stepData.status === "completed" && (
              <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                ✓ Finalizat
              </span>
            )}
            {stepData.status === "in_progress" && (
              <span className="text-xs font-medium text-blue-700 bg-blue-100 px-2 py-0.5 rounded animate-pulse">
                În lucru
              </span>
            )}
          </div>
          <h3 className="font-heading font-semibold text-slate-800">{config.title}</h3>
          <p className="md:text-sm text-xs text-slate-600 mt-1">
            {config.descriere}
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            {config.details.map((detail, idx) => (
              <span
                key={idx}
                className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full"
              >
                {detail}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Skeleton className="w-12 h-12 rounded-xl" />
            <div>
              <Skeleton className="h-7 w-64 mb-2" />
              <Skeleton className="h-5 w-48" />
            </div>
          </div>
        </div>
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-4">
            <Skeleton className="h-6 w-32 mb-4" />
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-40 rounded-xl" />
            ))}
          </div>
          <Skeleton className="h-[500px] rounded-2xl" />
        </div>
      </div>
    </div>
  );
}

function NoSessionSelected({
  onNavigate,
}: {
  onNavigate: (path: string) => void;
}) {
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-5xl mx-auto">
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <Info size={24} />
              Selectați o companie
            </CardTitle>
            <CardDescription className="text-amber-700">
              Pentru a vedea progresul notificării NIS2, trebuie să selectați
              sau să creați o companie.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <button
              onClick={() => onNavigate("/nis2-wizard")}
              className="bg-amber-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-amber-700 transition-colors"
            >
              Începe Notificarea
            </button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

const GhidNotificare = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { showOnboardingModal } = useOnboardingModal();
  const { currentSessionId, isLoadingSessions } = useSessionSwitcher();
  const { steps, isLoading, hasSession, entityData } =
    useNotificationGuideProgress(currentSessionId);

  const handleNavigate = (path: string) => {
    if (
      path === "/identificare-raportare" &&
      getNotificationPdfStatus(entityData) !== "completed"
    ) {
      showOnboardingModal();
      return;
    }
    navigate(path);
  };

  if (isLoading || isLoadingSessions) {
    return <LoadingSkeleton />;
  }

  if (!hasSession) {
    return <NoSessionSelected onNavigate={navigate} />;
  }

  const stepDataMap: Record<number, NotificationStep> = {
    1: steps.step1,
    2: steps.step2,
    3: steps.step3,
    4: steps.step4,
    5: steps.step5,
  };

  return (
    <div className="container mx-auto px-4 md:py-8 py-4">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="md:w-12 w-10 md:h-12 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
              <BookOpen
                size={isMobile ? 16 : 24}
                className="text-emerald-600"
              />
            </div>
            <div>
              <h1 className="font-heading md:text-2xl text-xl font-bold text-slate-800">
                Ghid Notificare și Identificare
              </h1>
              <p className="md:text-sm text-xs text-slate-600">
                Cum funcționează automatizarea NIS2PASS
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-4">
            <h2 className="font-heading font-semibold text-slate-800 text-lg mb-4">
              Pașii Aplicației
            </h2>

            {stepsConfig.map((config) => (
              <NotificationStepCard
                key={config.id}
                config={config}
                stepData={stepDataMap[config.id]}
              />
            ))}
          </div>

          <WorkflowPanel
            steps={steps}
            onNavigate={handleNavigate}
            entityData={entityData}
          />
        </div>

        <div className="md:mt-8 mt-4 bg-amber-50 rounded-2xl border border-amber-200 p-5">
          <h3 className="font-heading font-semibold md:text-lg text-base text-amber-800 flex items-center gap-2 mb-3">
            <AlertTriangle size={20} />
            Documente Necesare pentru Notificare
          </h3>
          <div className="grid md:grid-cols-3 gap-3">
            <div className="flex items-center gap-2 md:text-sm text-xs text-amber-700">
              <CheckSquare size={16} className="text-amber-600" />
              <span>Cifra de Afaceri (ultimul an)</span>
            </div>
            <div className="flex items-center gap-2 md:text-sm text-xs text-amber-700">
              <CheckSquare size={16} className="text-amber-600" />
              <span>Număr mediu salariați</span>
            </div>
            <div className="flex items-center gap-2 md:text-sm text-xs text-amber-700">
              <CheckSquare size={16} className="text-amber-600" />
              <span>Total Active (bilanț)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GhidNotificare;
